import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  TextInput,
  PasswordInput,
  Button,
  Alert,
  Anchor,
  SimpleGrid,
  Checkbox,
  Progress,
} from '@mantine/core';
import { IconAlertCircle, IconUserPlus } from '@tabler/icons-react';
import { useAuthService } from '@actschurch/ccms-sdk';

function passwordStrength(pw: string): { score: number; label: string; color: string } {
  let score = 0;
  if (pw.length >= 8) score += 25;
  if (/[A-Z]/.test(pw)) score += 25;
  if (/[0-9]/.test(pw)) score += 25;
  if (/[^A-Za-z0-9]/.test(pw)) score += 25;
  const label = score < 50 ? 'Weak' : score < 75 ? 'Fair' : score < 100 ? 'Good' : 'Strong';
  const color = score < 50 ? 'red' : score < 75 ? 'orange' : score < 100 ? 'yellow' : 'green';
  return { score, label, color };
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const authService = useAuthService();

  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [email, setEmail] = useState('');
  const [cellphone, setCellphone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const strength = passwordStrength(password);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (strength.score < 50) {
      setError('Please choose a stronger password (8+ chars, mix of cases / numbers).');
      return;
    }
    if (!acceptTerms) {
      setError('Please accept the terms to continue.');
      return;
    }

    setSubmitting(true);
    try {
      await authService.register({
        email: email.trim(),
        password,
        confirmPassword,
        firstName: firstName.trim(),
        surname: surname.trim(),
        cellphone: cellphone.trim() || undefined,
        appUrl: window.location.origin,
      });
      navigate(`/auth/confirm-email?email=${encodeURIComponent(email.trim())}`, { replace: true });
    } catch (err: any) {
      setError(err?.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container size={520} my={60}>
      <Title order={2} ta="center" mb="md">
        Create your account
      </Title>
      <Text c="dimmed" size="sm" ta="center" mb="xl">
        Sign up to access courses, the knowledge base, and member-only content.
      </Text>

      <Paper withBorder shadow="sm" p="lg" radius="md">
        <form onSubmit={onSubmit}>
          <Stack gap="md">
            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {error}
              </Alert>
            )}
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <TextInput
                label="First name"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.currentTarget.value)}
                autoComplete="given-name"
              />
              <TextInput
                label="Surname"
                required
                value={surname}
                onChange={(e) => setSurname(e.currentTarget.value)}
                autoComplete="family-name"
              />
            </SimpleGrid>
            <TextInput
              label="Email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              autoComplete="email"
            />
            <TextInput
              label="Cellphone"
              placeholder="+27..."
              value={cellphone}
              onChange={(e) => setCellphone(e.currentTarget.value)}
              autoComplete="tel"
            />
            <PasswordInput
              label="Password"
              required
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              autoComplete="new-password"
            />
            {password.length > 0 && (
              <Stack gap={4}>
                <Progress value={strength.score} color={strength.color} size="xs" />
                <Text size="xs" c="dimmed">{strength.label} password</Text>
              </Stack>
            )}
            <PasswordInput
              label="Confirm password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.currentTarget.value)}
              autoComplete="new-password"
            />
            <Checkbox
              label={
                <Text size="sm">
                  I accept the <Anchor href="/terms" target="_blank">Terms</Anchor> and{' '}
                  <Anchor href="/privacy" target="_blank">Privacy Policy</Anchor>.
                </Text>
              }
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.currentTarget.checked)}
            />
            <Button type="submit" loading={submitting} leftSection={<IconUserPlus size={16} />} fullWidth>
              Create account
            </Button>
          </Stack>
        </form>
        <Text size="sm" ta="center" mt="lg">
          Already have an account?{' '}
          <Anchor component={Link} to="/auth/login" fw={600}>
            Sign in
          </Anchor>
        </Text>
      </Paper>
    </Container>
  );
}
